import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header1";
import { useParams, useNavigate } from "react-router-dom";
import { API_URL } from "../../api";

// Reusable detail display box
const DetailItem = ({ label, value }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box mb={2}>
      <Typography variant="h6" color={colors.primary[700]}>
        {label}
      </Typography>
      <Box
        sx={{
          p: 2,
          border: `1px solid ${colors.grey[300]}`,
          borderRadius: "8px",
          backgroundColor: colors.primary[50],
        }}
      >
        <Typography variant="body1">{value ?? "N/A"}</Typography>
      </Box>
    </Box>
  );
};

const PromoCodeDetails = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams();
  const navigate = useNavigate();

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch details
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await fetch(`${API_URL}admin/promotioncode/${id}`);
        const data = await response.json();
        setDetails(data);
      } catch (error) {
        console.error("Error fetching promotion code details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  const handleDelete = useCallback(() => {
    fetch(`${API_URL}admin/promotioncode/delete/${id}`, { method: "DELETE" })
      .then(() => navigate("/promo-codes"))
      .catch(error => console.error("Error deleting promotional code:", error));
  }, [id, navigate]);

  const formatDate = value =>
    value ? new Intl.DateTimeFormat("en-US").format(new Date(value)) : "N/A";

  if (loading) {
    return (
      <Box m={4} textAlign="center">
        <CircularProgress />
        <Typography mt={2}>Loading promo code details...</Typography>
      </Box>
    );
  }

  if (!details) {
    return (
      <Box m={4}>
        <Typography variant="h6" color="error">
          Promo code not found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
          mb: 3,
        }}
      >
        <Header title="Promotional Code Details" />
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mt: { xs: 2, sm: 0 },
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate("/promo-codes")}
          >
            Back to List
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/promo-codes/edit/${id}`)}
          >
            Edit
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <DetailItem label="Promotion Code" value={details.promo_id} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DetailItem label="Amount Off" value={details.amount_off} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DetailItem label="Percent Off" value={details.percent_off} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DetailItem
            label="Available From"
            value={formatDate(details.available_from)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DetailItem
            label="Available To"
            value={formatDate(details.available_to)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DetailItem label="Max Redemptions" value={details.max_redemptions} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DetailItem
            label="Subscription Period"
            value={details.subscription_period}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DetailItem
            label="Once Per User"
            value={details.once_per_user ? "Yes" : "No"}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PromoCodeDetails;
